import type React from 'react';
import { shallowEqual } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import type { AvailableUpsellGroups } from 'react/checkout/Store/Upsell/state';
import type { RecentlyViewedItem } from 'react/checkout/Store/RecentlyViewedItem/state';
import type { CartItem } from 'react/checkout/Store/CartItem/state';
import { useTypedSelector, useTypedDispatch } from '../../Store/connectors';
import ErrorModal from '../ErrorModal/ErrorModal';
import EmptyCart from '../ShoppingBox/EmptyCart';
import QuickCheckout from '../QuickCheckout/QuickCheckout';
import FAQ from '../FAQ/FAQ';
import ContinueShopping from '../PriceOverview/ContinueShopping';
import Overview from '../Overview/Overview';
import RecentlyViewedContainer from '../RecentlyViewed/RecentlyViewedContainer';
import {
  removeCartItem,
  removeCartItemMessage,
  setCartItemAmount,
} from '../../Store/CartItem/action';
import Translation from '../../../general/Translation/Translation';
import PriceOverview from '../PriceOverview/PriceOverview';
import Section from '../../../general/Section/Section';
import 'react-toastify/dist/ReactToastify.css';
import { useWithStaticUrl } from '../../../general/WebshopContext/WebshopContext';
import ErrorLogger from '../../../../js/error-logger/ErrorLogger';
import type {
  FaqState,
  Price,
} from '../../../../ssr/src/Outlet/ShoppingCart/ShoppingCartSchema';

const ShoppingCartPage: React.FC = () => {
  const [
    items,
    faqItems,
    shippingPrice,
    subTotal,
    total,
    availableUpsellGroups,
    editorFontCacheVersion,
    hasError,
    recentItems,
  ] = useTypedSelector(
    ({
      cartItems,
      faqs,
      metaData,
      prices,
      recentlyViewedItems,
      upsells,
    }): [
      Record<string, CartItem>,
      FaqState['faqItems'],
      Price,
      Price,
      Price,
      AvailableUpsellGroups,
      string | null,
      boolean,
      RecentlyViewedItem[],
    ] => [
      cartItems.items,
      faqs.faqItems,
      prices.shippingPrice,
      prices.subTotal,
      prices.total,
      upsells.groups,
      upsells.editorFontCacheVersion,
      metaData.hasError,
      recentlyViewedItems.recentItems,
    ],
    shallowEqual,
  );

  const dispatch = useTypedDispatch();
  const updateProductAmount = (id: string, amount: string) =>
    dispatch(setCartItemAmount({ amount, id }));
  const deleteProduct = (id: string) => dispatch(removeCartItem({ id }));
  const deleteProductMessage = (id: string) =>
    dispatch(removeCartItemMessage({ id }));
  const withStaticUrl = useWithStaticUrl();

  const containsPersonalisedUpsells = () => {
    const availableUpsellGroupsList = Object.values(availableUpsellGroups);
    return (
      typeof availableUpsellGroupsList.find((groups) =>
        groups.find((group) =>
          group.products.find((product) => product.personalized),
        ),
      ) !== 'undefined'
    );
  };

  const error = hasError && <ErrorModal />;

  if (Object.keys(items).length === 0) {
    return (
      <>
        {error}
        <EmptyCart />
        {!!recentItems.length && (
          <RecentlyViewedContainer products={recentItems} />
        )}
        <FAQ faqs={faqItems} />
      </>
    );
  }

  let fontStylesheets = null;

  if (containsPersonalisedUpsells()) {
    if (!editorFontCacheVersion) {
      ErrorLogger.createFromGlobals()?.log(
        new Error(
          'Attempted to load Editor font stylesheets without version number',
        ),
      );
    }

    fontStylesheets = (
      <>
        <link
          rel="stylesheet"
          type="text/css"
          href={withStaticUrl(
            `/resourcebase/font/fonts.${editorFontCacheVersion ?? '0'}.css`,
          )}
          data-testid="editor-fonts"
        />
        <link
          rel="stylesheet"
          type="text/css"
          href={withStaticUrl(
            `/fonts/fallback-fonts/stylesheet.${editorFontCacheVersion ?? '0'}.css`,
          )}
          data-testid="editor-fallback-fonts"
        />
      </>
    );
  }

  return (
    <>
      {fontStylesheets}
      {
        <ToastContainer
          theme="dark"
          className="notifications"
          hideProgressBar={true}
        />
      }
      {error}
      <Section isSm>
        <QuickCheckout total={total} />
        <ContinueShopping showOnMobile={false} />
        <div className="page-heading hide-xs">
          <h1>
            <Translation
              pageString="checkout"
              stringId="shoppingcart-title"
            />
          </h1>
        </div>
      </Section>
      <Section isSm>
        <div className="is-grid has-col-3-lg has-col-1 has-col-gap-md has-no-col-gap-lg">
          <div className="is-col-2">
            <Overview
              cartItems={items}
              availableUpsellGroups={availableUpsellGroups}
              onCartItemAmountChange={updateProductAmount}
              onCartItemDelete={deleteProduct}
              onCartItemMessageDelete={deleteProductMessage}
            />
          </div>
          <PriceOverview
            shippingPrice={shippingPrice}
            total={total}
            subTotal={subTotal}
          />
        </div>
      </Section>
      {!!recentItems.length && (
        <RecentlyViewedContainer products={recentItems} />
      )}
      <FAQ faqs={faqItems} />
    </>
  );
};

export default ShoppingCartPage;
